import { Button } from "@oc-labs/wt-storybook";
import useModal from "../../../hooks/useModal";
import { Plus } from "lucide-react";
import MethodSystem from "../../../components/method-system";
import { useEffect, useRef, useState } from "react";
import OnrampCard from "./OnrampCard";
import OfframpCard from "./OfframpCard";
import CheckoutCard from "./CheckoutCard";
import ConfirmModal from "../../../components/modals/ConfirmModal";
import useSmartContracts from "../../../hooks/useSmartContracts";
import useRamp from "../../../hooks/useRamp";
import useAPI from "../../../hooks/useAPI";
import toast from "react-hot-toast";

export default function AdminRamp() {
  const { open } = useModal("method-system");
  const { adminSmartContracts } = useSmartContracts();
  const { rampOperators, adminRampMethods, adminDeleteRampMethod } = useRamp();
  const { getCurrencies } = useAPI();
  const [operators, setOperators] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [methods, setMethods] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [contractFilter, setContractFilter] = useState("all");
  const [networkFilter, setNetworkFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef(null);
  const { open: openConfirm } = useModal("confirm-modal");

  useEffect(() => {
    adminSmartContracts()
      .then((contracts) => {
        setContracts(contracts);
      })
      .catch((err) => console.error(err));

    rampOperators()
      .then((operators) => {
        setOperators(operators);
      })
      .catch((err) => console.error(err));

    getCurrencies()
      .then((currencies) => {
        setCurrencies(currencies);
      })
      .catch((err) => console.error(err));

    loadMethods();
  }, []);

  const loadMethods = () => {
    adminRampMethods()
      .then((methods) => {
        setMethods(methods);
      })
      .catch((err) => console.error(err));
  };

  const deleteMethod = (id) => {
    openConfirm({
      title: "Delete Method",
      description:
        "Are you sure you want to delete this method? This action cannot be undone.",
      onConfirm: () => {
        adminDeleteRampMethod(id)
          .then(() => {
            loadMethods();
            toast.success("Method deleted successfully");
          })
          .catch((err) => {
            console.error(err);
          });
      },
    });
  };

  const methodTypeLabels = {
    onramp: "Onramp",
    offramp: "Offramp",
    checkout: "Checkout",
  };

  const contractKey = (contract) =>
    contract?.id != null
      ? String(contract.id)
      : `${contract?.name || ""}|${contract?.network?.name || ""}`;

  const networkKey = (network) =>
    network?.id != null ? String(network.id) : network?.name || "";

  const typeOptions = [
    ...new Set(methods.map((method) => method.type).filter(Boolean)),
  ];

  const contractOptionsByKey = new Map();

  methods.forEach((method) => {
    if (!method.contract) return;

    const key = contractKey(method.contract);
    if (contractOptionsByKey.has(key)) return;

    contractOptionsByKey.set(key, {
      key,
      name: method.contract.name || "Unnamed contract",
      networkName: method.contract.network?.name || "",
    });
  });

  const contractOptions = Array.from(contractOptionsByKey.values()).sort((a, b) =>
    `${a.name} ${a.networkName}`.localeCompare(`${b.name} ${b.networkName}`)
  );

  const networkOptionsByKey = new Map();

  methods.forEach((method) => {
    const network = method.contract?.network;
    if (!network) return;

    const key = networkKey(network);
    if (!key || networkOptionsByKey.has(key)) return;

    networkOptionsByKey.set(key, {
      key,
      name: network.name || "Unnamed network",
    });
  });

  const networkOptions = Array.from(networkOptionsByKey.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const handleSearchVisible = () => {
    setShowSearch((prev) => {
      const next = !prev;
      if (next && searchRef.current) searchRef.current.focus();
      return next;
    });
  };

  const filteredMethods = methods.filter((method) => {
    const matchesType = typeFilter === "all" || method.type === typeFilter;
    const matchesContract =
      contractFilter === "all" || contractKey(method.contract) === contractFilter;
    const matchesNetwork =
      networkFilter === "all" ||
      networkKey(method.contract?.network) === networkFilter;
    const searchText = [
      methodTypeLabels[method.type],
      method.type,
      method.contract?.name,
      method.contract?.network?.name,
      ...(method.operators || []).map((operator) => operator.operatorId),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const matchesSearch = searchText.includes(search.toLowerCase());

    return matchesType && matchesContract && matchesNetwork && matchesSearch;
  });

  return (
    <div>
      <MethodSystem
        operators={operators}
        contracts={contracts}
        currencies={currencies}
        reloadMethods={loadMethods}
      />
      <ConfirmModal />

      <div className="mb-3 flex w-full items-end justify-between gap-4">
        <div className="flex min-w-0 flex-wrap items-end gap-4">
          <label className="flex flex-col gap-1 text-xs font-medium uppercase tracking-wider text-gray-500">
            Type
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="form-select w-36 rounded-md border border-gray-300 bg-white px-2 py-1 pr-3.5 text-xs font-normal normal-case tracking-normal text-gray-700 focus:outline-0 focus:ring-2 focus:ring-primary"
            >
              <option value="all">All</option>
              {typeOptions.map((type) => (
                <option key={type} value={type}>
                  {methodTypeLabels[type] || type}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs font-medium uppercase tracking-wider text-gray-500">
            Contract
            <select
              value={contractFilter}
              onChange={(e) => setContractFilter(e.target.value)}
              className="form-select w-72 rounded-md border border-gray-300 bg-white px-2 py-1 pr-3.5 text-xs font-normal normal-case tracking-normal text-gray-700 focus:outline-0 focus:ring-2 focus:ring-primary"
            >
              <option value="all">All</option>
              {contractOptions.map((contract) => (
                <option key={contract.key} value={contract.key}>
                  {contract.name}
                  {contract.networkName ? ` (${contract.networkName})` : ""}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs font-medium uppercase tracking-wider text-gray-500">
            Network
            <select
              value={networkFilter}
              onChange={(e) => setNetworkFilter(e.target.value)}
              className="form-select w-48 rounded-md border border-gray-300 bg-white px-2 py-1 pr-3.5 text-xs font-normal normal-case tracking-normal text-gray-700 focus:outline-0 focus:ring-2 focus:ring-primary"
            >
              <option value="all">All</option>
              {networkOptions.map((network) => (
                <option key={network.key} value={network.key}>
                  {network.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <div className="relative">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              onFocus={() => setShowSearch(true)}
              onBlur={() => setShowSearch(false)}
              className={`pl-10 pr-4 py-[6px] h-[32px] bg-gray-100 rounded-full text-sm ${
                showSearch ? "w-72" : "w-32"
              } outline-none transition-all duration-300`}
            />
            <img
              src="/icons/search.svg"
              alt="Search"
              className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 cursor-pointer"
              onClick={handleSearchVisible}
            />
          </div>
          <Button onClick={() => open()} size="small" type="secondary">
            Create Method <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full table-fixed">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[17%]">
                  Contract
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[7%]">
                  KYC
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%]">
                  Operators
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[16%]">
                  Details
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%]">
                  Created
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[26%]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMethods.length === 0 && (
                <tr>
                  <td className="px-4 py-8 text-center text-sm text-gray-500" colSpan={7}>
                    No methods found.
                  </td>
                </tr>
              )}
              {filteredMethods.map((method) => {
                const methodCards = {
                  onramp: OnrampCard,
                  offramp: OfframpCard,
                  checkout: CheckoutCard,
                };
                const View = methodCards[method.type];
                if (!View)
                  return (
                    <div key={method.id}>
                      Unsupported method type: {method.type}
                    </div>
                  );
                return (
                  <tr
                    key={method.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <View
                      key={method.id}
                      method={method}
                      operators={operators}
                      deleteMethod={deleteMethod}
                    />
                  </tr>
                );
              })}
            </tbody>
          </table>
      </div>
    </div>
  );
}
